import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Lock, ArrowLeft, Loader2, CheckCircle, Eye, EyeOff, Check, X, AlertTriangle } from 'lucide-react';
import api from '@/lib/api';

const EVA_LOGO_URL = "/eva-icon-square.png";

const PASSWORD_REQUIREMENTS = [
  { label: 'At least 8 characters', test: (p) => p.length >= 8 },
  { label: 'One uppercase letter (A-Z)', test: (p) => /[A-Z]/.test(p) },
  { label: 'One lowercase letter (a-z)', test: (p) => /[a-z]/.test(p) },
  { label: 'One digit (0-9)', test: (p) => /\d/.test(p) },
  { label: 'One special character', test: (p) => /[@$!%*?&#^()_+\-=[\]{}|;:,.<>]/.test(p) },
];

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [token] = useState(() => searchParams.get('token') || window.history.state?.evaToken || '');

  // EIP-433: the token is a single-use secret that arrived in the URL, so it
  // must not stay in the address bar, the history entry, or any Referer this
  // page sends. Captured into state first so everything below keeps working,
  // then also parked in history.state so a refresh mid-form still has it
  // instead of landing on the invalid-link card. Raw replaceState rather than
  // the router's navigate: this rewrites the current entry only and leaves
  // react-router's own state (idx, key) intact by spreading it.
  useEffect(() => {
    if (!searchParams.get('token') || !token) return;
    window.history.replaceState(
      { ...window.history.state, evaToken: token },
      document.title,
      window.location.pathname
    );
  }, [searchParams, token]);
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);

  const passwordStrength = PASSWORD_REQUIREMENTS.filter(req => req.test(password)).length;
  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;
  const allRequirementsMet = passwordStrength === PASSWORD_REQUIREMENTS.length;

  // Check if token is present
  if (!token) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-secondary flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-12 pb-12 text-center">
            <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertTriangle className="w-8 h-8 text-destructive" />
            </div>
            <h2 className="text-2xl font-bold text-foreground mb-3">Invalid Reset Link</h2>
            <p className="text-muted-foreground mb-8">
              This password reset link is invalid or has expired.
            </p>
            <Link to="/forgot-password">
              <Button className="bg-primary hover:bg-primary/90">
                Request New Link
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!allRequirementsMet) {
      toast.error('Password does not meet requirements');
      return;
    }
    if (!passwordsMatch) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      await api.post('/auth/reset-password', { 
        token, 
        new_password: password,
        product: 'eva_insights',
      });
      setSuccess(true);
      toast.success('Password reset successful!', { description: 'You can now log in with your new password.' });
      
      // Redirect to login after delay
      setTimeout(() => {
        navigate('/login');
      }, 3000);
      
    } catch (err) {
      toast.error('Reset failed', { description: err.response?.data?.detail || 'This link may have expired. Please request a new one.' });
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-secondary flex items-center justify-center p-4">
        <Card className="w-full max-w-md text-center">
          <CardContent className="pt-12 pb-12">
            <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-success" />
            </div>
            <h2 className="text-2xl font-bold text-foreground mb-3">Password Reset!</h2>
            <p className="text-muted-foreground mb-5">
              Your password has been updated successfully. Redirecting to login...
            </p>
            <Loader2 className="w-5 h-5 animate-spin mx-auto text-primary" />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-secondary flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center pb-6">
          {/* Logo */}
          <img src={EVA_LOGO_URL} alt="Eva" className="w-12 h-12 rounded-xl mx-auto mb-5" />
          <CardTitle className="text-2xl font-bold">Set new password</CardTitle>
          <CardDescription className="mt-1.5">
            Create a strong password for your account
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Password */}
            <div className="space-y-2">
              <Label htmlFor="password" className="flex items-center gap-2">
                <Lock className="w-4 h-4 text-muted-foreground" />
                New Password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Create a strong password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onFocus={() => setPasswordFocused(true)}
                  onBlur={() => setTimeout(() => setPasswordFocused(false), 200)}
                  data-testid="reset-password"
                  required
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-muted-foreground"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              
              {/* Password Strength Indicator */}
              {(passwordFocused || password.length > 0) && (
                <div className="mt-2 p-3 bg-secondary rounded-lg border border-border">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                      <div 
                        className={`h-full transition-all duration-300 ${
                          passwordStrength <= 2 ? 'bg-destructive/100' : 
                          passwordStrength <= 3 ? 'bg-warning' : 
                          passwordStrength <= 4 ? 'bg-info/100' : 'bg-success/100'
                        }`}
                        style={{ width: `${(passwordStrength / PASSWORD_REQUIREMENTS.length) * 100}%` }}
                      />
                    </div>
                    <span className="text-xs text-muted-foreground w-16">
                      {passwordStrength <= 2 ? 'Weak' : 
                       passwordStrength <= 3 ? 'Fair' : 
                       passwordStrength <= 4 ? 'Good' : 'Strong'}
                    </span>
                  </div>
                  <ul className="space-y-1">
                    {PASSWORD_REQUIREMENTS.map((req, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-xs">
                        {req.test(password) ? (
                          <Check className="w-3 h-3 text-success" />
                        ) : (
                          <X className="w-3 h-3 text-muted-foreground" />
                        )}
                        <span className={req.test(password) ? 'text-success' : 'text-muted-foreground'}>
                          {req.label}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Confirm Password */}
            <div className="space-y-2">
              <Label htmlFor="confirm_password" className="flex items-center gap-2">
                <Lock className="w-4 h-4 text-muted-foreground" />
                Confirm Password
              </Label>
              <div className="relative">
                <Input
                  id="confirm_password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="Confirm your password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  data-testid="reset-confirm-password"
                  required
                  className={`pr-10 ${
                    confirmPassword.length > 0 
                      ? passwordsMatch 
                        ? 'border-success focus:border-success' 
                        : 'border-destructive focus:border-destructive'
                      : ''
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-muted-foreground"
                  tabIndex={-1}
                >
                  {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {confirmPassword.length > 0 && !passwordsMatch && (
                <p className="text-xs text-destructive">Passwords do not match</p>
              )}
            </div>

            {/* Submit Button */}
            <Button 
              type="submit" 
              className="w-full bg-primary hover:bg-primary/90"
              disabled={loading || !allRequirementsMet || !passwordsMatch}
              data-testid="reset-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Resetting...
                </>
              ) : (
                'Reset Password'
              )}
            </Button>

            {/* Back to Login */}
            <div className="text-center pt-2">
              <Link 
                to="/login" 
                className="text-sm text-muted-foreground hover:text-primary inline-flex items-center gap-1"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to login
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
